import { useMutation, useQuery } from "@apollo/client/react"
import { ArrowRight, Check, Scissors } from "lucide-react"
import { useRef, useState } from "react"
import { gql } from "~/__generated__"
import { RecordingCleanupPreviewFragment } from "~/__generated__/graphql"
import { cn } from "~/common/cn"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"

gql(/* GraphQL */ `
  fragment RecordingCleanupPreview on RecordingCleanup {
    id
    mode
    status
    error
    version
    originalDuration
    duration
    originalUrl
    videoUrl
    sourceCurrent
    notes
    cuts {
      id
      start
      end
      kind
      text
      enabled
    }
  }
`)

const CLEANUPS = gql(/* GraphQL */ `
  query PostRecordingCleanups($id: ID!) {
    editorialWorkspace {
      post(id: $id) {
        id
        editable
        recordingCleanups {
          ...RecordingCleanupPreview
        }
      }
    }
  }
`)

const CREATE_CLEANUP = gql(/* GraphQL */ `
  mutation CreateRecordingCleanup($input: RecordingCleanupCreateInput!) {
    recordingCleanupCreate(input: $input) {
      cleanup {
        ...RecordingCleanupPreview
      }
    }
  }
`)

const UPDATE_CLEANUP = gql(/* GraphQL */ `
  mutation UpdateRecordingCleanup($input: RecordingCleanupUpdateInput!) {
    recordingCleanupUpdate(input: $input) {
      cleanup {
        ...RecordingCleanupPreview
      }
    }
  }
`)

const modes = [
  {
    id: "natural",
    label: "Natural",
    description: "Light trimming. Keep more breathing room between thoughts.",
  },
  {
    id: "tight",
    label: "Tight",
    description: "A balanced pace. Shorten longer pauses while keeping your delivery.",
  },
  {
    id: "very_tight",
    label: "Very tight",
    description: "A faster pace. Review the joins carefully before creating clips.",
  },
] as const

type CleanupMode = (typeof modes)[number]["id"]
const busyStatuses = ["queued", "analyzing", "rendering"]
const statusLabels: Record<string, string> = {
  queued: "Cleanup is queued…",
  analyzing: "Finding pauses and possible cuts…",
  rendering: "Building your cleaned preview…",
  failed: "Cleanup needs attention",
  ready: "Your preview is ready",
}

function durationLabel(seconds?: number | null) {
  if (seconds == null) return "—"
  const rounded = Math.max(0, Math.floor(seconds))
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, "0")}`
}

function cutTime(seconds: number) {
  return `${durationLabel(seconds)}.${Math.floor(seconds * 10) % 10}`
}

function cutKindLabel(kind: string) {
  if (kind.includes("restart")) return "Possible restart"
  if (kind.includes("filler")) return "Possible filler"
  if (kind.includes("silence") || kind.includes("pause")) return "Pause"
  return "Suggested cut"
}

export function CleanupPanel({
  postId,
  hasRecordings,
  clipBusy,
  reapConfigured,
  onCreateClips,
}: {
  postId: string
  hasRecordings: boolean
  clipBusy: boolean
  reapConfigured: boolean
  onCreateClips: (cleanupId: string, cleanupVersion: number) => Promise<void>
}) {
  const { data, error, loading, refetch } = useQuery(CLEANUPS, {
    variables: { id: postId },
    pollInterval: 5000,
  })
  const [create, creation] = useMutation(CREATE_CLEANUP)
  const [mode, setMode] = useState<CleanupMode>("tight")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<Error | null>(null)
  const post = data?.editorialWorkspace.post
  const cleanups = post?.recordingCleanups ?? []
  const cleanup = cleanups.find((item) => item.id === selectedId) ?? cleanups[0]
  const working = creation.loading || cleanups.some((item) => busyStatuses.includes(item.status))
  if (data && !post?.editable) return null
  return (
    <section
      className="mt-5 overflow-hidden rounded-xl border bg-background"
      aria-label="Recording cleanup"
    >
      <div className="border-b bg-primary/5 p-5">
        <div className="flex items-center gap-2">
          <Scissors className="size-4 text-primary" />
          <h4 className="font-semibold">A cleaner recording, still your voice</h4>
        </div>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
          Tighten pauses before Reap chooses your clips. Review every suggested cut and compare the
          preview with your original. Your saved sections stay intact.
        </p>
      </div>
      <div className="space-y-5 p-5">
        <fieldset disabled={working || clipBusy || !post?.editable}>
          <legend className="mb-3 text-sm font-medium">Choose the pace</legend>
          <div className="grid gap-3 xl:grid-cols-3">
            {modes.map((item) => (
              <label
                key={item.id}
                className={cn(
                  "flex cursor-pointer gap-3 rounded-lg border p-3 text-sm transition-colors",
                  mode === item.id ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                  (working || clipBusy) && "cursor-default opacity-70"
                )}
              >
                <input
                  type="radio"
                  name={`cleanup-mode-${postId}`}
                  value={item.id}
                  checked={mode === item.id}
                  onChange={() => setMode(item.id)}
                  className="mt-1 accent-primary"
                />
                <span>
                  <span className="font-medium">
                    {item.label}
                    {item.id === "tight" && (
                      <span className="block text-xs text-primary">Recommended</span>
                    )}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                    {item.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <Button
            disabled={!hasRecordings || !post?.editable || working || clipBusy}
            onClick={async () => {
              setActionError(null)
              try {
                const result = await create({ variables: { input: { postId, mode } } })
                if (result.error) throw result.error
                const created = result.data?.recordingCleanupCreate.cleanup
                if (!created) throw new Error("Cleanup did not start. Please try again.")
                setSelectedId(created.id)
                await refetch()
              } catch (err) {
                setActionError(err instanceof Error ? err : new Error("Could not start cleanup"))
              }
            }}
          >
            <Scissors className="size-4" />
            {working ? "Preparing cleanup…" : "Clean up recording"}
          </Button>
          {!hasRecordings && (
            <p className="mt-2 text-xs text-muted-foreground">
              Record or upload an included section to get started.
            </p>
          )}
        </div>
        {loading && !data && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading recording previews…
          </p>
        )}
        {cleanups.length > 1 && (
          <div>
            <label htmlFor={`cleanup-version-${postId}`} className="text-sm font-medium">
              Preview history
            </label>
            <select
              id={`cleanup-version-${postId}`}
              value={cleanup?.id ?? ""}
              onChange={(event) => setSelectedId(event.target.value)}
              className="mt-2 block w-full rounded-lg border bg-background p-2 text-sm"
            >
              {cleanups.map((item, index) => (
                <option key={item.id} value={item.id}>
                  {modes.find((option) => option.id === item.mode)?.label ?? item.mode} ·{" "}
                  {index === 0 ? "Latest preview" : `Earlier preview ${index}`} · {item.status}
                </option>
              ))}
            </select>
          </div>
        )}
        {cleanup && (
          <CleanupReview
            key={cleanup.id}
            cleanup={cleanup}
            clipBusy={clipBusy}
            reapConfigured={reapConfigured}
            onSaved={async () => {
              await refetch()
            }}
            onCreateClips={onCreateClips}
          />
        )}
        {(error || actionError) && <GraphqlError error={(actionError ?? error)!} />}
      </div>
    </section>
  )
}

function CleanupReview({
  cleanup,
  clipBusy,
  reapConfigured,
  onSaved,
  onCreateClips,
}: {
  cleanup: RecordingCleanupPreviewFragment
  clipBusy: boolean
  reapConfigured: boolean
  onSaved: () => Promise<void>
  onCreateClips: (cleanupId: string, cleanupVersion: number) => Promise<void>
}) {
  const originalVideo = useRef<HTMLVideoElement>(null)
  const [update, updating] = useMutation(UPDATE_CLEANUP)
  const [draft, setDraft] = useState<{ version: number; ids: string[] } | null>(null)
  const [reviewed, setReviewed] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const enabledIds = cleanup.cuts.filter((cut) => cut.enabled).map((cut) => cut.id)
  const chosenIds = draft?.ids ?? enabledIds
  const dirty =
    chosenIds.length !== enabledIds.length || chosenIds.some((id) => !enabledIds.includes(id))
  const changedElsewhere = draft != null && draft.version !== cleanup.version
  const working = updating.loading || busyStatuses.includes(cleanup.status)
  const ready = cleanup.status === "ready" && !!cleanup.videoUrl
  const short = cleanup.duration != null && cleanup.duration < 120
  const reviewKey = `${cleanup.id}:${cleanup.version}`
  const canSubmit =
    ready &&
    !working &&
    !clipBusy &&
    !submitting &&
    !dirty &&
    !changedElsewhere &&
    cleanup.sourceCurrent &&
    !short &&
    cleanup.duration != null &&
    reapConfigured &&
    reviewed === reviewKey
  const savedSeconds =
    ready && cleanup.originalDuration != null && cleanup.duration != null
      ? Math.max(0, cleanup.originalDuration - cleanup.duration)
      : null
  return (
    <div className="space-y-4 border-t pt-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">
            {modes.find((mode) => mode.id === cleanup.mode)?.label ?? cleanup.mode} preview
          </p>
          <p role="status" className="mt-1 text-sm text-muted-foreground">
            {statusLabels[cleanup.status] ?? "Preparing your preview"}
          </p>
        </div>
        {savedSeconds != null && (
          <p className="rounded-full bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
            {durationLabel(cleanup.originalDuration)} <ArrowRight className="mx-1 inline size-3" />{" "}
            {durationLabel(cleanup.duration)} · {durationLabel(savedSeconds)} shorter
          </p>
        )}
      </div>
      {!cleanup.sourceCurrent && (
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          Your included recordings changed after this cleanup started. Create a new cleanup before
          sending a cleaned recording to Reap.
        </p>
      )}
      {changedElsewhere && (
        <div className="rounded-lg border border-amber-300 p-3 text-sm">
          <p>
            This preview changed elsewhere. Load its latest cut selections before rebuilding or
            creating clips.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => {
              setDraft(null)
              setReviewed(null)
            }}
          >
            Load latest selections
          </Button>
        </div>
      )}
      {cleanup.error && (
        <p role="alert" className="text-sm text-destructive">
          {cleanup.error}
        </p>
      )}
      {cleanup.notes.length > 0 && (
        <ul className="space-y-1 text-sm leading-6 text-muted-foreground">
          {cleanup.notes.map((note, index) => (
            <li key={`${index}-${note}`}>{note}</li>
          ))}
        </ul>
      )}
      {(cleanup.originalUrl || cleanup.videoUrl) && (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-sm font-medium">
              Original · {durationLabel(cleanup.originalDuration)}
            </p>
            {cleanup.originalUrl ? (
              <video
                ref={originalVideo}
                aria-label="Original recording preview"
                src={cleanup.originalUrl}
                controls
                playsInline
                preload="metadata"
                className="aspect-video w-full rounded-lg bg-black"
              />
            ) : (
              <p className="text-sm text-muted-foreground">Original preview is being prepared.</p>
            )}
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">
              Cleaned · {ready ? durationLabel(cleanup.duration) : "Preparing…"}
            </p>
            {cleanup.videoUrl && ready ? (
              <video
                aria-label="Cleaned recording preview"
                src={cleanup.videoUrl}
                controls
                playsInline
                preload="metadata"
                className="aspect-video w-full rounded-lg bg-black"
              />
            ) : (
              <div className="flex aspect-video items-center justify-center rounded-lg bg-muted p-5 text-center text-sm text-muted-foreground">
                Your cleaned preview will appear here when it is ready.
              </div>
            )}
          </div>
        </div>
      )}
      {cleanup.cuts.length > 0 && (
        <div>
          <h5 className="text-sm font-semibold">Review the cuts</h5>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Checked sections are removed. Suggested filler words and restarts need your review;
            leave any phrase you want to keep unchecked. Times refer to the original recording.
          </p>
          <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
            {cleanup.cuts.map((cut) => (
              <div
                key={cut.id}
                className={cn(
                  "flex flex-col gap-2 rounded-lg border p-3 text-sm sm:flex-row sm:items-start sm:gap-3",
                  chosenIds.includes(cut.id) && "bg-primary/5"
                )}
              >
                <label className="flex min-w-0 flex-1 items-start gap-3">
                  <input
                    aria-label={`Remove ${cutKindLabel(cut.kind).toLowerCase()} at ${cutTime(cut.start)}`}
                    type="checkbox"
                    checked={chosenIds.includes(cut.id)}
                    disabled={
                      working ||
                      clipBusy ||
                      submitting ||
                      changedElsewhere ||
                      !cleanup.sourceCurrent
                    }
                    className="mt-1 accent-primary"
                    onChange={(event) => {
                      setReviewed(null)
                      setDraft({
                        version: draft?.version ?? cleanup.version,
                        ids: event.target.checked
                          ? [...chosenIds, cut.id]
                          : chosenIds.filter((id) => id !== cut.id),
                      })
                    }}
                  />
                  <span>
                    <span className="font-medium">{cutKindLabel(cut.kind)}</span>
                    <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                      {cutTime(cut.start)}–{cutTime(cut.end)}
                    </span>
                    {cut.text && (
                      <span className="mt-1 block whitespace-pre-wrap leading-6 text-muted-foreground">
                        {cut.text}
                      </span>
                    )}
                  </span>
                </label>
                {cleanup.originalUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-6 self-start sm:ml-0"
                    onClick={() => {
                      const video = originalVideo.current
                      if (!video) return
                      video.currentTime = Math.max(0, cut.start - 1)
                      video.scrollIntoView({ behavior: "smooth", block: "center" })
                      void video.play().catch(() => {
                        setError(
                          new Error("Press play on the original recording to hear this moment.")
                        )
                      })
                    }}
                  >
                    Play this moment
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {dirty && (
        <p className="text-sm text-primary">
          Your cut selections changed. Rebuild the preview to hear them before creating clips.
        </p>
      )}
      {(cleanup.cuts.length > 0 || (cleanup.status === "failed" && cleanup.originalUrl)) && (
        <Button
          variant="outline"
          disabled={
            working ||
            clipBusy ||
            submitting ||
            !cleanup.sourceCurrent ||
            changedElsewhere ||
            (!dirty && cleanup.status !== "failed")
          }
          onClick={async () => {
            setError(null)
            setReviewed(null)
            try {
              const result = await update({
                variables: {
                  input: {
                    id: cleanup.id,
                    expectedVersion: draft?.version ?? cleanup.version,
                    enabledCutIds: chosenIds,
                  },
                },
              })
              if (result.error) throw result.error
              if (!result.data) throw new Error("Could not rebuild this preview")
              setDraft(null)
              await onSaved()
            } catch (err) {
              setError(err instanceof Error ? err : new Error("Could not rebuild preview"))
            }
          }}
        >
          {working
            ? "Building preview…"
            : cleanup.status === "failed"
              ? "Retry cleanup preview"
              : "Rebuild preview"}
        </Button>
      )}
      {ready && (
        <div className="rounded-xl border bg-muted/30 p-4">
          {short && (
            <p className="mb-3 text-sm text-amber-700">
              This cleaned recording is under Reap’s 2-minute minimum. Keep more footage, choose a
              lighter cleanup, or use the original if it is long enough.
            </p>
          )}
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={reviewed === reviewKey}
              disabled={dirty || working || changedElsewhere || !cleanup.sourceCurrent || short}
              className="mt-1 accent-primary"
              onChange={(event) => setReviewed(event.target.checked ? reviewKey : null)}
            />
            <span>I reviewed this cleaned preview and want to use it for clips.</span>
          </label>
          <Button
            className="mt-4 h-auto whitespace-normal py-2"
            disabled={!canSubmit}
            onClick={async () => {
              setError(null)
              setSubmitting(true)
              try {
                await onCreateClips(cleanup.id, cleanup.version)
              } catch (err) {
                setError(
                  err instanceof Error ? err : new Error("Could not create clips from this preview")
                )
              } finally {
                setSubmitting(false)
              }
            }}
          >
            <Check className="size-4" />
            {submitting || clipBusy
              ? "Clips are processing…"
              : "Create clips from reviewed cleanup"}
          </Button>
        </div>
      )}
      {error && <GraphqlError error={error} />}
    </div>
  )
}
